import { map, upperCase } from "lodash";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";

import { InputState } from "react-input-mask";
import { Time } from "shared/assets/icons";
import { Action } from "shared/components";
import { colors } from "shared/styles/constants";
import { IFormFieldStyle } from "shared/types";
import {
  AM_PM,
  TimeFormat,
  getTwelveHourTime,
  getTwentyFourHourTime,
} from "shared/utils";
import { FieldWrapper, FieldWrapperProps } from "../FieldWrapper";
import { InputFieldWrapper } from "../styles";
import {
  PrefixIcon,
  SimpleDateTimeFieldContainer,
  StyledAmPmToggle,
  StyledInputTimeMask,
} from "./styles";

interface SimpleTimeFieldProps extends FieldWrapperProps {
  control: Control;
  timeFormat?: TimeFormat;
  validation?: RegisterOptions;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  defaultValue?: string;
  style?: IFormFieldStyle;
  onChange?: (e: any) => void;
  onClick?: () => void;
}

/*
    This is a very weird form field that has given me a lot of headaches (Vincent).
    The masked value is used to display the value in the input field, but the actual value is used to store the value in the form.
    
        (e.g. When the timeFormat is AM/PM the value that the client sees in the form can be 02:30 PM (maskedValue), but the actual value is 14:30:00 (value))

    The reason for this is because the masked value is not a valid time format, but the actual value is.
    There is also a lot of formatting going on behind the scenes in the formatDateTime function in the shared/utils folder. For example the conversion from 12 to 24 hour time and vice versa.
    The onChange/setCheckTime function is used to check if the start and end date/times are correct and are passed to the component in the Form that calls this component in its getFormFields method.
    The reason why this is such a hassle to implement is because the defaultValue that is passed to this component is linked to the ACTUAL value of the field.

    If you have any questions, feel free to ask me (@VincentRavoetWieni on Github).
*/
export const SimpleTimeField2: React.FC<SimpleTimeFieldProps> = ({
  control,
  name,
  validation,
  defaultValue,
  error,
  disabled,
  timeFormat,
  style,
  onChange: setCheckTime,
  tabIndex,
  ...rest
}) => {
  const [amPm, setAmPm] = useState<AM_PM>(AM_PM.am);

  const isTwelveHour = useMemo(
    () => timeFormat === TimeFormat.twelve,
    [timeFormat]
  );

  const renderAmPmToggle = useCallback(
    (onChange: (option: AM_PM) => void) => {
      return (
        <StyledAmPmToggle
          spaceDirection="horizontal"
          size="xxs"
          flexWrap="nowrap"
        >
          {map(AM_PM, (option) => {
            return (
              <Action
                key={option}
                tabIndex={tabIndex && tabIndex + 1}
                label={upperCase(option)}
                isActive={amPm !== option} // this is done intentionally because style looks better
                handleClick={() => {
                  onChange(option);
                  setAmPm(option);
                }}
              />
            );
          })}
        </StyledAmPmToggle>
      );
    },
    [amPm, tabIndex]
  );

  const formatChars = useMemo(
    () => ({
      "1": isTwelveHour ? "[0-1]" : "[0-2]",
      "2": "[0-9]",
      "3": "[0-5]",
      "4": "[0-9]",
    }),
    [isTwelveHour]
  );

  const beforeMaskedValueChange = useCallback(
    (newState: InputState, oldState: InputState, userInput: any) => {
      const { value } = newState;

      let maskedValue = value;

      if (
        (((oldState.selection?.start === 0 ||
          oldState.selection?.start === 1) &&
          maskedValue[0] === "_" &&
          maskedValue[1] === "_") ||
          (oldState.selection?.start === 0 &&
            (oldState.selection.end === 2 || oldState.selection.end === 5))) &&
        userInput &&
        userInput.length === 1
      ) {
        if (
          (isTwelveHour && parseInt(userInput) > 1) ||
          (!isTwelveHour && parseInt(userInput) > 2)
        ) {
          if (oldState.selection?.start === 0 && oldState.selection.end === 5) {
            maskedValue = `0${userInput}:__`;
          } else {
            maskedValue = `0${userInput}:${maskedValue[3]}${maskedValue[4]}`;
          }
        }
      }

      let endSelection = maskedValue.indexOf("_");

      if (isTwelveHour) {
        if (value.startsWith("1")) {
          formatChars["2"] = "[0-2]";
        } else {
          formatChars["2"] = "[1-9]";
        }
      } else {
        if (value.startsWith("2")) {
          formatChars["2"] = "[0-3]";
        } else {
          formatChars["2"] = "[0-9]";
        }
      }

      return {
        ...newState,
        value: maskedValue,
        selection: {
          start: endSelection,
          end: endSelection,
        },
      };
    },
    [formatChars, isTwelveHour]
  );

  const renderedMaskValue = useCallback(
    (value: string) => {
      if (!isTwelveHour || !value) return value;

      const { time, amPm } = getTwelveHourTime(value);

      setAmPm(amPm);

      return time;
    },
    [isTwelveHour]
  );

  const defaultVal = useRef(defaultValue);

  return (
    <SimpleDateTimeFieldContainer style={style}>
      <Controller
        control={control}
        name={name}
        rules={validation}
        defaultValue={defaultVal.current}
        render={({ onChange, value }) => {
          const maskValue = renderedMaskValue(value);

          return (
            <FieldWrapper name={name} error={error} {...rest}>
              <InputFieldWrapper isDisabled={disabled} error={!!error}>
                <PrefixIcon as={Time} color={colors.grey4} size="xl" />
                <StyledInputTimeMask
                  id={name}
                  value={maskValue}
                  mask={"12:34"}
                  formatChars={formatChars}
                  beforeMaskedValueChange={beforeMaskedValueChange}
                  placeholder="--:--"
                  maskChar={"_"}
                  type="text"
                  onChange={(event: any) => {
                    event.persist();

                    let userTime = event.target.value;

                    if (isTwelveHour && userTime.indexOf("_") === -1) {
                      const twentyFourHour = getTwentyFourHourTime({
                        time: userTime,
                        amPm,
                      });

                      setCheckTime && setCheckTime(twentyFourHour);
                      onChange(twentyFourHour);
                    } else {
                      const newTime =
                        userTime && userTime.indexOf("_") === -1
                          ? userTime + ":00"
                          : userTime;

                      setCheckTime && setCheckTime(newTime);
                      onChange(newTime);
                    }
                  }}
                  tabIndex={tabIndex}
                />
                {isTwelveHour &&
                  renderAmPmToggle((option: AM_PM) => {
                    const twentyFourHourTime = getTwentyFourHourTime({
                      time: maskValue,
                      amPm: option,
                    });

                    setCheckTime && setCheckTime(twentyFourHourTime);
                    onChange(twentyFourHourTime);
                  })}
              </InputFieldWrapper>
            </FieldWrapper>
          );
        }}
      />
    </SimpleDateTimeFieldContainer>
  );
};
